// language.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Language } from '../models/language';
import { map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private languagesUrl = 'assets/languages.json';
  private languagesCache$: Observable<Language[]>;

  constructor(private http: HttpClient, private translate: TranslateService) {
    // Initialize the cache
    this.languagesCache$ = this.http.get<Language[]>(this.languagesUrl).pipe(
      shareReplay(1)
    );
  }

  getLanguages(): Observable<Language[]> {
    return this.languagesCache$;
  }

  getLanguageName(languageCode: string): Observable<string> {
    return of(this.getLocalizedLanguageName(languageCode));
  }

  getLocalizedLanguageName(languageCode: string): string {
    if (!languageCode) {
      return '';
    }

    const translationKey = `languages.${languageCode}`;
    const translated = this.translate.instant(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }

    return this.translate.instant('languages.unknown');
  }

  storedLanguage: string = localStorage.getItem('websiteLanguage');
  private selectedLanguageSubject: BehaviorSubject<string> = new BehaviorSubject<string>(this.storedLanguage || 'en');
  selectedLanguage$: Observable<string> = this.selectedLanguageSubject.asObservable();

  setSelectedLanguage(languageCode: string): void {
    this.selectedLanguageSubject.next(languageCode);
  }

  private userLanguageSubject = new BehaviorSubject<string>(null);
  getUserLanguage$ = this.userLanguageSubject.asObservable();

  getLanguageCode(languageName: string): Observable<string> {
    return this.languagesCache$.pipe(
      map((languages) => {
        const byCode = languages.find((lang) => lang.value === languageName);
        if (byCode) {
          return byCode.value;
        }

        const byEnglishName = languages.find((lang) => lang.text === languageName);
        if (byEnglishName) {
          return byEnglishName.value;
        }

        const byLocalizedName = languages.find(
          (lang) => this.getLocalizedLanguageName(lang.value) === languageName
        );
        return byLocalizedName ? byLocalizedName.value : '';
      })
    );
  }
}
